/**
 * VideoInterstitial — fullscreen click-to-play intro video.
 *
 * The user must click the play button to start playback. Once playing, the
 * video runs to completion with native controls hidden. The only normal
 * dismissal path is the natural onEnded event. There is no autoplay, no
 * muted attribute, no silent dismiss, and no watchdog timers.
 *
 * Three view states:
 *   - 'idle'    : black overlay, large centred play button, caption. The
 *                 <video> element is mounted with preload="auto" so the
 *                 browser can pre-buffer, but it is not playing.
 *   - 'playing' : <video> fills the overlay, native controls hidden, audio
 *                 enabled. The video plays through to completion.
 *   - 'error'   : "Intro video unavailable today" message with a "Continue
 *                 to dashboard" button. This is the ONLY exit from the
 *                 overlay other than natural video completion, and it
 *                 exists so a broken video never traps the user.
 *
 * Day-consumed semantics: markIntroVideoPlayedToday is called on the first
 * play click AND on error, so a broken state cannot trap the user on every
 * subsequent login — the SAST gate in AuthProvider.selectRole recognises
 * today's mark and does not show the overlay again until tomorrow.
 *
 * Props:
 *   videoUrl   (string)   — URL of the video to play
 *   onComplete (function) — called when the video ends or the user clicks
 *                           Continue from the error state
 */

import React, { useRef, useState, useCallback } from 'react';
import { markIntroVideoPlayedToday } from '../../api/userProfilesApi';
import { getTodaySASTDateString } from '../../utils/dateHelpers';
import { useAuth } from '../../auth/AuthProvider';

export default function VideoInterstitial({ videoUrl, onComplete }) {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const markedRef = useRef(false);
  const [view, setView] = useState('idle');

  const markPlayedOnce = useCallback(() => {
    if (markedRef.current) return;
    markedRef.current = true;
    if (user?.id) {
      markIntroVideoPlayedToday(user.id, getTodaySASTDateString());
    }
  }, [user?.id]);

  const handlePlay = useCallback(() => {
    markPlayedOnce();
    setView('playing');
    const v = videoRef.current;
    if (v) {
      const promise = v.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => {
          setView('error');
        });
      }
    }
  }, [markPlayedOnce]);

  const handleError = useCallback(() => {
    const v = videoRef.current;
    const err = v?.error || null;
    console.error('[Inbox intro] video error', {
      code: err?.code ?? null,
      message: err?.message ?? null,
      currentSrc: v?.currentSrc ?? null,
      networkState: v?.networkState ?? null,
      readyState: v?.readyState ?? null,
    });
    markPlayedOnce();
    setView('error');
  }, [markPlayedOnce]);

  const handleEnded = useCallback(() => {
    if (onComplete) onComplete();
  }, [onComplete]);

  const handleContinue = useCallback(() => {
    if (onComplete) onComplete();
  }, [onComplete]);

  return (
    <div style={styles.container}>
      <video
        ref={videoRef}
        src={videoUrl}
        style={view === 'playing' ? styles.video : styles.videoHidden}
        playsInline
        preload="auto"
        controls={false}
        onEnded={handleEnded}
        onError={handleError}
      />

      {view === 'idle' && (
        <div style={styles.idleOverlay}>
          <button
            type="button"
            onClick={handlePlay}
            aria-label="Play intro video"
            style={styles.playButton}
          >
            <svg width="36" height="36" viewBox="0 0 36 36" aria-hidden="true">
              <polygon points="11,7 29,18 11,29" fill="#1A2B3C" />
            </svg>
          </button>
          <p style={styles.caption}>Welcome to Inbox — click play to start</p>
        </div>
      )}

      {view === 'error' && (
        <div style={styles.errorOverlay}>
          <p style={styles.errorMessage}>Intro video unavailable today</p>
          <button
            type="button"
            onClick={handleContinue}
            style={styles.continueButton}
          >
            Continue to dashboard
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    position: 'fixed',
    inset: 0,
    background: '#000000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    cursor: 'default',
  },
  videoHidden: {
    display: 'none',
  },
  idleOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
    color: '#FFFFFF',
  },
  playButton: {
    width: 96,
    height: 96,
    borderRadius: '50%',
    background: '#FFFFFF',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
  },
  caption: {
    margin: 0,
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.85)',
    letterSpacing: 0.2,
  },
  errorOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
    color: '#FFFFFF',
    padding: 24,
    textAlign: 'center',
  },
  errorMessage: {
    margin: 0,
    fontSize: 18,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  continueButton: {
    background: 'var(--cp-secondary)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 10,
    padding: '12px 24px',
    fontSize: 15,
    cursor: 'pointer',
    fontWeight: 600,
  },
};
